import {useEffect, useMemo, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';

import LoadingIndicator from '../../../components/loading-indicator';
import {AppContent, ContentRow} from '../../../styled/components';
import {ActionsRow, ContentRowHeader, CountryContainer, FormRow} from './country-form.styles';
import {getCountry, saveCountry, updateCountry} from '../api-service';
import {normalizeCountry, validateCountry} from './country-validation';

const emptyCountry = {
  name: '',
  code: '',
  currency: '',
  phoneCode: '',
  vatLabel: '',
};

const allFieldsTouched = Object.keys(emptyCountry).reduce(
  (touched, field) => ({...touched, [field]: true}),
  {},
);

function CountryForm({countryId, onCancel, onSaved}) {
  const isEditMode = countryId !== null && countryId !== 'new';
  const [formData, setFormData] = useState(emptyCountry);
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState(null);

  const title = isEditMode ? `Countries / ${formData?.name || ''}` : 'Countries / New';
  const errors = useMemo(() => validateCountry(formData), [formData]);
  // A field only complains once the user has left it (or tried to save), so
  // an untouched empty form does not open with a wall of red.
  const errorFor = (field) => (touched[field] ? errors[field] : undefined);

  useEffect(() => {
    if (!isEditMode) {
      setFormData(emptyCountry);
      setTouched({});
      return;
    }
    setLoading(true);
    getCountry(countryId)
      .then((country) => {
        if (!country) {
          setServerError(`Country ${countryId} not found`);
          return;
        }
        setFormData({
          ...emptyCountry,
          ...country,
          code: country.code || '',
          currency: country.currency || '',
          phoneCode: country.phoneCode || '',
          vatLabel: country.vatLabel || '',
        });
        setTouched({});
      })
      .catch((err) => setServerError(err.message || 'Error loading country'))
      .finally(() => setLoading(false));
  }, [countryId, isEditMode]);

  const handleFieldChange = (ev) => {
    const {name, value} = ev.target;
    setFormData((prev) => ({...prev, [name]: value}));
  };

  const handleFieldBlur = (ev) => {
    const {name} = ev.target;
    setTouched((prev) => ({...prev, [name]: true}));
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (Object.keys(errors).length > 0) {
      setTouched(allFieldsTouched);
      return;
    }
    setLoading(true);
    setServerError(null);
    try {
      const payload = normalizeCountry(formData);
      if (isEditMode) {
        const updated = await updateCountry(countryId, payload);
        onSaved?.(updated ?? {id: countryId});
      } else {
        const created = await saveCountry(payload);
        onSaved?.(created);
      }
    } catch (err) {
      setServerError(err.message || 'Error saving country');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingIndicator/>}
      <AppContent>
        <ContentRow sx={ContentRowHeader}>
          <Typography variant="h5" component="h1">{title}</Typography>
          <Box sx={ActionsRow}>
            <Button variant="outlined" color="error" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={loading}
              onClick={handleSubmit}
            >
              {isEditMode ? 'Update' : 'Save'}
            </Button>
          </Box>
        </ContentRow>
        {serverError && (
          <ContentRow>
            <Alert severity="error" onClose={() => setServerError(null)}>{serverError}</Alert>
          </ContentRow>
        )}
        <ContentRow>
          <Box component="form" sx={CountryContainer} onSubmit={handleSubmit} noValidate>
            <Box sx={FormRow}>
              <TextField
                name="name"
                label="Country Name"
                value={formData.name}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={Boolean(errorFor('name'))}
                helperText={errorFor('name')}
                fullWidth
                required
              />
              <TextField
                name="code"
                label="Country Code"
                value={formData.code}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={Boolean(errorFor('code'))}
                helperText={errorFor('code') || 'ISO 3166-1 alpha-2, e.g. AT'}
                fullWidth
                inputProps={{maxLength: 10}}
              />
            </Box>
            <Box sx={FormRow}>
              <TextField
                name="currency"
                label="Currency"
                value={formData.currency}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={Boolean(errorFor('currency'))}
                helperText={errorFor('currency') || 'ISO 4217 code, e.g. EUR'}
                fullWidth
                inputProps={{maxLength: 10}}
              />
              <TextField
                name="phoneCode"
                label="Country Calling Code"
                value={formData.phoneCode}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={Boolean(errorFor('phoneCode'))}
                helperText={errorFor('phoneCode') || 'Digits only, e.g. 43'}
                fullWidth
                inputProps={{inputMode: 'numeric', maxLength: 6}}
              />
              <TextField
                name="vatLabel"
                label="VAT Label"
                value={formData.vatLabel}
                onChange={handleFieldChange}
                onBlur={handleFieldBlur}
                error={Boolean(errorFor('vatLabel'))}
                helperText={errorFor('vatLabel')}
                fullWidth
              />
            </Box>
          </Box>
        </ContentRow>
      </AppContent>
    </>
  );
}

export default CountryForm;
